/**
 * 前端统一 API 访问层：集中处理超时、取消、重试、JSON/SSE 响应解析和用户可读错误。
 * 业务组件不要直接调用 fetch，避免错误提示和重复提交策略不一致。
 */
import { formatUserFacingBrand } from '@/utils/modelDisplay'

const DEFAULT_TIMEOUT_MS = 45000

function isRequestTimeout(error) {
  if (!error || typeof error !== 'object') return false
  const name = String(error.name || '')
  const message = String(error.message || '')
  return name === 'AbortError'
    || name === 'TimeoutError'
    || /aborted\s+due\s+to\s+timeout|operation\s+was\s+aborted|signal\s+timed\s+out/i.test(message)
}

function statusFallback(status) {
  if (status === 400) return ['提交内容不正确', '请检查图片、选项和描述后重新提交。']
  if (status === 401) return ['登录状态已失效', '请重新登录，未完成的生成任务仍会保留。']
  if (status === 403) return ['当前账号无权执行此操作', '请刷新页面后重试；若仍失败，请联系管理员检查账号权限。']
  if (status === 404) return ['请求的记录或图片不存在', '请刷新页面后重新选择。']
  if (status === 408 || status === 504) return ['服务响应超时', '任务可能仍在后台处理，请稍后继续查询。']
  if (status === 409) return ['当前数据已发生变化', '请刷新页面获取最新状态后重试。']
  if (status === 413) return ['图片超过 10MB 上限', '请压缩图片或降低分辨率至 10MB 以内后重新上传。']
  if (status === 429) return ['当前使用人数较多，服务暂时繁忙', '请等待 30 秒后重试，避免连续点击。']
  if (status >= 500) return ['服务暂时不可用', '请稍后重试；已提交的生成任务可继续查询，无需重复创建。']
  return [`请求失败（${status}）`, '请检查输入内容和网络后重新尝试。']
}

export function normalizeApiError(error, fallbackMessage = '请求失败，请稍后重试') {
  // 统一返回 message/code/suggestion/pending/terminal，组件只根据这些字段更新界面。
  if (error && error.normalized) return error
  const source = error && typeof error === 'object' ? error : {}
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false
  const aborted = isRequestTimeout(source)
  const networkError = source instanceof TypeError || source.code === 'NETWORK_ERROR'
  const result = new Error(formatUserFacingBrand(
    offline
      ? '当前网络已断开'
      : aborted
        ? (source.timeoutMessage || '请求等待超时')
        : networkError
          ? '无法连接服务器，请检查网络后重试'
        : (typeof error === 'string' ? error : source.message) || fallbackMessage
  ))
  result.code = source.code || (offline ? 'OFFLINE' : aborted ? 'REQUEST_TIMEOUT' : networkError ? 'NETWORK_ERROR' : 'REQUEST_FAILED')
  result.status = Number(source.status || 0)
  result.suggestion = formatUserFacingBrand(source.suggestion || (
    offline
      ? '请恢复网络连接后重试；已经提交的生成任务不会丢失。'
      : aborted
        ? '请检查网络后重试；若正在生成图片，请优先继续查询，避免重复提交。'
        : networkError
          ? '无法连接服务器，请检查网络或稍后重试。'
          : '请检查输入内容后重新尝试。'
  ))
  result.retryable = typeof source.retryable === 'boolean'
    ? source.retryable
    : offline || aborted || networkError || result.status === 408 || result.status === 429 || result.status >= 500
  result.pending = Boolean(source.pending)
  result.terminal = Boolean(source.terminal)
  result.normalized = true
  return result
}

function parseResponseBody(text, contentType) {
  if (!text) return {}
  if (!String(contentType || '').toLowerCase().includes('json')) return { raw: text }
  try {
    return JSON.parse(text)
  } catch (error) {
    const invalid = new Error('服务器返回了无法识别的数据')
    invalid.code = 'INVALID_RESPONSE'
    invalid.suggestion = '请稍后重试；若持续出现，请联系管理员检查服务日志。'
    invalid.retryable = true
    throw invalid
  }
}

function addController(controllers, controller) {
  if (Array.isArray(controllers)) controllers.push(controller)
}

function removeController(controllers, controller) {
  if (!Array.isArray(controllers)) return
  const index = controllers.indexOf(controller)
  if (index >= 0) controllers.splice(index, 1)
}

export async function apiRequest(url, options = {}, context = {}) {
  // GET 默认允许有限重试；生成类 POST 默认不自动重试，避免网络抖动造成重复任务。
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retries,
    retryDelayMs = 700,
    timeoutMessage = '请求等待超时',
    ...fetchOptions
  } = options
  const method = String(fetchOptions.method || 'GET').toUpperCase()
  const retryCount = retries == null ? (method === 'GET' ? 1 : 0) : Math.max(0, Number(retries) || 0)
  const controllers = context.controllers
  const isStopped = typeof context.isStopped === 'function' ? context.isStopped : () => false
  let lastError = null

  for (let attempt = 0; attempt <= retryCount; attempt += 1) {
    if (isStopped()) throw normalizeApiError(new Error('页面已关闭，请重新打开后继续操作'))
    const controller = new AbortController()
    addController(controllers, controller)
    const timer = window.setTimeout(() => controller.abort(), Math.max(1000, Number(timeoutMs) || DEFAULT_TIMEOUT_MS))
    try {
      const response = await window.fetch(url, { ...fetchOptions, signal: controller.signal })
      const text = await response.text()
      const result = parseResponseBody(text, response.headers.get('content-type'))
      if (!response.ok) {
        const fallback = statusFallback(response.status)
        const requestError = new Error(result.message || fallback[0])
        requestError.code = result.code || `HTTP_${response.status}`
        requestError.status = response.status
        requestError.suggestion = result.suggestion || fallback[1]
        requestError.retryable = typeof result.retryable === 'boolean'
          ? result.retryable
          : response.status === 408 || response.status === 429 || response.status >= 500
        if (['INSUFFICIENT_POINTS', 'VIP_REQUIRED', 'ENTERPRISE_MEMBERSHIP_REQUIRED'].includes(requestError.code)) {
          window.dispatchEvent(new CustomEvent('insufficient-points', { detail: result }))
        }
        throw requestError
      }
      if (method === 'POST' && String(url).startsWith('/api/generations')) window.dispatchEvent(new CustomEvent('points-updated'))
      return result
    } catch (error) {
      if (isRequestTimeout(error)) error.timeoutMessage = timeoutMessage
      const requestError = normalizeApiError(error)
      lastError = requestError
      if (attempt >= retryCount || !requestError.retryable || isStopped()) throw requestError
    } finally {
      window.clearTimeout(timer)
      removeController(controllers, controller)
    }
    await new Promise(resolve => window.setTimeout(resolve, retryDelayMs * (attempt + 1)))
  }
  throw lastError || normalizeApiError(null)
}

export async function downloadResource(url, options = {}) {
  // 下载结果图的专用入口，复用同一套超时和错误归一化。
  const timeoutMs = Math.max(1000, Number(options.timeoutMs) || 30000)
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)
  try {
    const source = String(url || '').trim()
    if (!source) throw new Error('没有可下载的图片')
    let requestUrl = source
    if (/^https?:\/\//i.test(source)) {
      const parsed = new URL(source)
      if (parsed.origin !== window.location.origin) {
        requestUrl = `/api/images/download?url=${encodeURIComponent(source)}`
      }
    }
    const response = await window.fetch(requestUrl, { signal: controller.signal })
    if (!response.ok) {
      const error = new Error(`图片下载失败（${response.status}）`)
      error.status = response.status
      error.code = `DOWNLOAD_HTTP_${response.status}`
      error.retryable = response.status === 408 || response.status === 429 || response.status >= 500
      throw error
    }
    const blob = await response.blob()
    if (!blob || !blob.size) {
      const error = new Error('服务器返回了空图片')
      error.code = 'EMPTY_DOWNLOAD'
      error.retryable = true
      throw error
    }
    return blob
  } catch (error) {
    if (isRequestTimeout(error)) error.timeoutMessage = '图片下载超时'
    throw normalizeApiError(error, '图片下载失败')
  } finally {
    window.clearTimeout(timer)
  }
}

export async function postMultipartResource(url, formData, options = {}) {
  // 供抠图等“POST 后直接返回二进制图片”的接口使用。
  const timeoutMs = Math.max(1000, Number(options.timeoutMs) || 120000)
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await window.fetch(url, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    })
    if (!response.ok) {
      const text = await response.text()
      let details = {}
      try {
        details = text ? JSON.parse(text) : {}
      } catch (ignored) {
        details = {}
      }
      const fallback = statusFallback(response.status)
      const error = new Error(details.message || fallback[0])
      error.status = response.status
      error.code = details.code || `HTTP_${response.status}`
      error.suggestion = details.suggestion || fallback[1]
      error.retryable = typeof details.retryable === 'boolean'
        ? details.retryable
        : response.status === 408 || response.status === 429 || response.status >= 500
      throw error
    }
    const blob = await response.blob()
    if (!blob || !blob.size) {
      const error = new Error('抠图服务返回了空图片')
      error.code = 'EMPTY_CUTOUT'
      error.retryable = true
      throw error
    }
    return blob
  } catch (error) {
    if (isRequestTimeout(error)) error.timeoutMessage = '专业抠图等待超时'
    throw normalizeApiError(error, '专业抠图失败')
  } finally {
    window.clearTimeout(timer)
  }
}
